import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { useCart, lineKey } from "@/lib/cart";
import { displaySku, useSEO } from "@/lib/seo";
import { SITE } from "@/lib/site";
import { FinancingNote } from "@/components/TrustBar";
import { trackViewCart, trackBeginCheckout, trackAddShippingInfo } from "@/lib/analytics";
import { Trash2, Lock, ShoppingBag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export default function CartPage() {
  const cart = useCart();
  useSEO({ title: "Your Cart | Gold Time Jewelry", canonicalPath: "/cart", noindex: true });
  const [checkingOut, setCheckingOut] = useState(false);
  const [delivery, setDelivery] = useState("ship");
  const [showroom, setShowroom] = useState(SITE.locations[0].name);

  useEffect(() => {
    if (cart.items.length > 0) trackViewCart(cart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeDelivery = (value) => {
    setDelivery(value);
    if (cart.items.length > 0) trackAddShippingInfo(cart, value === "pickup" ? "Showroom pickup" : "Insured shipping");
  };

  const checkout = async () => {
    setCheckingOut(true);
    trackBeginCheckout(cart);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: cart.items.map((i) => ({ sku: i.sku, quantity: i.quantity, ...(i.size ? { size: i.size } : {}), ...(i.engrave ? { engrave: i.engrave } : {}), ...(i.plate ? { plate_text: i.plate } : {}) })),
        origin_url: window.location.origin,
        delivery,
        ...(delivery === "pickup" ? { showroom } : {}),
        ...(localStorage.getItem("gt_cart_token") ? { cart_token: localStorage.getItem("gt_cart_token") } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed — please try again or call us.");
      setCheckingOut(false);
    }
  };

  return (
    <main data-testid="cart-page">
      <Header />
      <section className="pt-[164px] pb-24 lg:pt-[196px] lg:pb-32 min-h-[60vh]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>Your Cart</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="cart-heading">
              Review your <span className="text-[#C9A227]">pieces</span>
            </h1>
          </Reveal>

          {cart.items.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="mt-16 text-center" data-testid="cart-empty">
                <ShoppingBag size={40} strokeWidth={1} className="mx-auto text-[#C9A227]" />
                <p className="text-neutral-600 mt-6">Your cart is empty.</p>
                <Link
                  to="/catalog"
                  data-testid="cart-browse-link"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] transition-[transform] duration-300 hover:-translate-y-0.5"
                >
                  Browse the catalog
                </Link>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <div className="mt-12 space-y-5" data-testid="cart-items">
                {cart.items.map((item) => (
                  <div
                    key={lineKey(item.sku, item.size, item.engrave, item.plate)}
                    data-testid={`cart-item-${item.sku}`}
                    className="flex gap-5 bg-white border border-[#E8DCC4] rounded-sm p-4 lg:p-5"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 lg:w-28 lg:h-28 object-cover rounded-sm shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg text-neutral-900 leading-snug truncate">{item.name}</h3>
                      <p className="text-xs text-neutral-400 mt-1" data-testid={`cart-style-${item.sku}`}>Style {displaySku(item.sku)}</p>
                      {item.size_label && (
                        <p className="text-xs text-neutral-600 mt-0.5 font-medium" data-testid={`cart-size-label-${item.sku}`}>{item.size_label}</p>
                      )}
                      {item.size && (
                        <p className="text-xs text-[#A8871B] mt-1 font-medium" data-testid={`cart-sizing-${item.sku}`}>
                          Sized to {item.size} (+{money(item.sizing_fee)}) — sized rings are non-refundable
                        </p>
                      )}
                      {item.engrave && (
                        <p className="text-xs text-[#A8871B] mt-1 font-medium" data-testid={`cart-engraving-${item.sku}`}>
                          Engraving: &ldquo;{item.engrave}&rdquo; (+{money(item.engraving_fee)}) — adds approx. 3–5 business days to processing; engraved pieces are final sale
                        </p>
                      )}
                      {item.plate && (
                        <p className="text-xs text-[#A8871B] mt-1 font-medium" data-testid={`cart-plate-${item.sku}`}>
                          Name plate: &ldquo;{item.plate}&rdquo; — made to order, final sale
                        </p>
                      )}
                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex items-center border border-[#E8DCC4] rounded-full">
                          <button
                            data-testid={`cart-minus-${item.sku}`}
                            onClick={() => cart.update(item.sku, item.quantity - 1, item.size, item.engrave, item.plate)}
                            className="w-8 h-8 text-neutral-600 hover:text-[#C9A227] transition-colors duration-300"
                          >
                            −
                          </button>
                          <span className="w-7 text-center text-sm font-semibold" data-testid={`cart-qty-${item.sku}`}>{item.quantity}</span>
                          <button
                            data-testid={`cart-plus-${item.sku}`}
                            onClick={() => cart.update(item.sku, item.quantity + 1, item.size, item.engrave, item.plate)}
                            className="w-8 h-8 text-neutral-600 hover:text-[#C9A227] transition-colors duration-300"
                          >
                            +
                          </button>
                        </div>
                        <button
                          data-testid={`cart-remove-${item.sku}`}
                          onClick={() => cart.update(item.sku, 0, item.size, item.engrave, item.plate)}
                          className="text-neutral-400 hover:text-red-500 transition-colors duration-300"
                          aria-label="Remove"
                        >
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <p className="font-display text-xl text-neutral-900 shrink-0" data-testid={`cart-line-total-${item.sku}`}>
                      {money(cart.lineTotal(item))}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-white border border-[#E8DCC4] rounded-sm p-6 lg:p-8" data-testid="cart-summary">
                <div className="flex justify-between text-base">
                  <span className="text-neutral-600">Subtotal</span>
                  <span className="font-display text-2xl text-neutral-900" data-testid="cart-subtotal">{money(cart.subtotal)}</span>
                </div>
                <div className="mt-6 space-y-2.5" data-testid="delivery-options">
                  <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">Delivery</p>
                  <label className="flex items-center gap-3 text-sm text-neutral-800 cursor-pointer" data-testid="delivery-ship">
                    <input
                      type="radio"
                      name="delivery"
                      checked={delivery === "ship"}
                      onChange={() => changeDelivery("ship")}
                      className="accent-[#C9A227]"
                    />
                    Ship directly to me — free insured US shipping
                  </label>
                  <label className="flex items-center gap-3 text-sm text-neutral-800 cursor-pointer" data-testid="delivery-pickup">
                    <input
                      type="radio"
                      name="delivery"
                      checked={delivery === "pickup"}
                      onChange={() => changeDelivery("pickup")}
                      className="accent-[#C9A227]"
                    />
                    Ship to a Gold Time showroom for pickup — free
                  </label>
                  {delivery === "pickup" && (
                    <select
                      value={showroom}
                      onChange={(e) => setShowroom(e.target.value)}
                      data-testid="pickup-showroom-select"
                      className="ml-7 bg-white border border-[#E8DCC4] rounded-sm px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[#C9A227]"
                    >
                      {SITE.locations.map((loc) => (
                        <option key={loc.name} value={loc.name}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-4">Taxes calculated at checkout. Every order is fully insured.</p>
                {cart.items.some((i) => i.engrave) && (
                  <p className="text-xs text-[#A8871B] font-medium mt-3" data-testid="cart-engraving-processing-note">
                    Your order includes engraving — it adds approximately 3–5 business days to processing.
                  </p>
                )}
                <div className="mt-4 bg-[#F5F3EC] border border-[#E8DCC4] rounded-sm px-4 py-3" data-testid="cart-financing-note">
                  <FinancingNote compact />
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={checkout}
                  disabled={checkingOut}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-4 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 disabled:opacity-60 disabled:translate-y-0"
                >
                  <Lock size={15} strokeWidth={1.5} />
                  {checkingOut ? "Redirecting to secure checkout..." : "Secure checkout"}
                </button>
              </div>
            </Reveal>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
